import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

export interface LowStockNotification {
  productId: string;
  productName: string;
  productCode: string;
  stock: number;
  type: "low" | "out";
  seen: boolean;
}

interface NotificationContextType {
  notifications: LowStockNotification[];
  unseenCount: number;
  markAllSeen: () => void;
  refreshNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<LowStockNotification[]>([]);
  const [ready, setReady] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("products")
        .select("id, name, product_code, stock")
        .eq("dealer_id", user.id)
        .lte("stock", 5);

      if (error) return;

      const newNotifications: LowStockNotification[] = (data || []).map((p: { id: string; name: string; product_code: string; stock: number }) => ({
        productId: p.id,
        productName: p.name,
        productCode: p.product_code || "",
        stock: p.stock || 0,
        type: (p.stock || 0) === 0 ? "out" : "low",
        seen: false,
      }));

      setNotifications((prev) => {
        const prevIds = new Set(prev.map((n) => n.productId));
        const newOnes = newNotifications.filter((n) => !prevIds.has(n.productId));
        const existing = prev
          .filter((n) => newNotifications.some((nn) => nn.productId === n.productId))
          .map((n) => {
            const updated = newNotifications.find((nn) => nn.productId === n.productId);
            return updated ? { ...n, stock: updated.stock, type: updated.type } : n;
          });
        return [...existing, ...newOnes.map((n) => ({ ...n, seen: false }))];
      });
    } catch {
      // silent fail - don't crash the app
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setReady(true);
      return;
    }

    let cancelled = false;
    const doFetch = async () => {
      await fetchNotifications();
      if (!cancelled) setReady(true);
    };
    doFetch();

    intervalRef.current = setInterval(fetchNotifications, 30000);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user?.id, fetchNotifications]);

  const markAllSeen = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, seen: true })));
  }, []);

  const unseenCount = notifications.filter((n) => !n.seen).length;

  if (!ready) {
    return (
      <NotificationContext.Provider value={{ notifications: [], unseenCount: 0, markAllSeen: () => {}, refreshNotifications: () => {} }}>
        {children}
      </NotificationContext.Provider>
    );
  }

  return (
    <NotificationContext.Provider value={{ notifications, unseenCount, markAllSeen, refreshNotifications: fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}